import axios from 'axios';

// Fonction pour obtenir l'URL de base de l'API
const getApiBaseUrl = () => {
  // 1. Vérifier si une variable d'environnement est définie
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // 2. Détecter automatiquement l'URL en fonction de l'environnement
  const hostname = window.location.hostname;
  
  // Si on est sur localhost, utiliser localhost pour l'API
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8000/api';
  }
  
  // Sinon, utiliser l'adresse IP de l'hôte actuel avec le port 8000
  return `http://${hostname}:8000/api`;
};

// Configuration de base de l'API
const API_BASE_URL = getApiBaseUrl();

// Fonction pour obtenir l'URL complète de l'endpoint de login
const getLoginUrl = () => {
  const baseUrl = API_BASE_URL.replace('/api', '');
  return `${baseUrl}/api-auth/token/login/`;
};

// Créer une instance axios avec la configuration par défaut
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
      // Debug: afficher le token dans la console (à supprimer en production)
      console.log('🔑 Token envoyé:', token.substring(0, 20) + '...');
      console.log('🔑 Header Authorization:', `Token ${token.substring(0, 20)}...`);
    } else {
      console.warn('⚠️ Aucun token trouvé dans localStorage');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    console.error('❌ Erreur API:', status, error.response?.data);
    
    // Seulement déconnecter en cas d'erreur 401 (Unauthorized)
    // L'erreur 403 (Forbidden) signifie que l'utilisateur n'a pas la permission,
    // mais il est toujours authentifié, donc on ne doit pas le déconnecter
    if (status === 401) {
      // Token invalide ou expiré
      console.error('🔴 Token invalide ou expiré. Redirection vers login...');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      window.location.href = '/login';
    } else if (status === 403) {
      // Permission refusée - l'utilisateur est authentifié mais n'a pas la permission
      // Ne pas déconnecter, juste logger l'erreur
      console.warn('⚠️ Permission refusée (403) pour:', error.config?.url);
      console.warn('L\'utilisateur est authentifié mais n\'a pas la permission d\'accéder à cette ressource');
    }
    
    return Promise.reject(error);
  }
);

// Service pour l'authentification
export const authService = {
  login: async (username, password) => {
    // Utiliser l'endpoint Django REST Framework pour obtenir un token
    // Note: on utilise axios directement car cet endpoint n'est pas sous /api/
    const loginUrl = getLoginUrl();
    console.log('🔗 URL de login:', loginUrl);
    const response = await axios.post(loginUrl, 
      { username, password },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  },
  
  loginWithToken: async (token) => {
    // Vérifier si le token est valide en faisant une requête test
    // On utilise une instance axios temporaire avec le token
    const testApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`
      },
    });
    
    try {
      await testApi.get('/patients/');
      return { token, valid: true };
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Token non valide');
      }
      throw error;
    }
  },
  
  getCurrentUser: async () => {
    // Récupérer les informations de l'utilisateur connecté
    try {
      console.log('🔍 Tentative de récupération des données utilisateur...');
      const token = authService.getToken();
      console.log('🔑 Token présent:', !!token);
      
      const response = await api.get('/staff/me/');
      console.log('✅ Données utilisateur récupérées avec succès:', response.data);
      
      // Vérifier que les données sont valides
      if (!response.data || !response.data.user) {
        console.warn('⚠️ Réponse API incomplète:', response.data);
        throw new Error('Réponse API incomplète');
      }
      
      // Vérifier que le rôle est présent
      if (!response.data.role) {
        console.warn('⚠️ Rôle non trouvé dans la réponse, utilisation du fallback');
        response.data.role = 'admin'; // Fallback
        response.data.role_display = 'Administrateur';
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de l\'utilisateur:', error);
      console.error('Status:', error.response?.status);
      console.error('Status Text:', error.response?.statusText);
      console.error('Data:', error.response?.data);
      console.error('Headers:', error.response?.headers);
      
      // Si c'est une erreur 404, l'utilisateur n'a peut-être pas de profil staff
      if (error.response?.status === 404) {
        console.warn('⚠️ Profil staff non trouvé (404), création de données minimales');
        // Retourner des données minimales pour permettre la connexion
        const token = authService.getToken();
        if (token) {
          return {
            user: {
              username: 'user',
              id: null,
              email: '',
              first_name: '',
              last_name: '',
            },
            staff: null,
            role: 'admin', // Fallback par défaut
            role_display: 'Administrateur',
          };
        }
      }
      
      // Si c'est une erreur 403 (Forbidden), problème de permissions
      if (error.response?.status === 403) {
        console.error('🔒 Erreur 403: Accès refusé. Vérifiez les permissions de l\'utilisateur.');
        // Retourner des données minimales pour permettre la connexion
        return {
          user: {
            username: 'user',
            id: null,
            email: '',
            first_name: '',
            last_name: '',
          },
          staff: null,
          role: 'admin', // Fallback par défaut
          role_display: 'Administrateur',
        };
      }
      
      // Propager l'erreur pour qu'elle soit gérée par le composant
      throw error;
    }
  },
  
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
  },
  
  setToken: (token) => {
    localStorage.setItem('authToken', token);
  },
  
  setUser: (userData) => {
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
      if (userData.role) {
        localStorage.setItem('userRole', userData.role);
      }
    }
  },
  
  getUser: () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        return null;
      }
    }
    return null;
  },
  
  getToken: () => {
    return localStorage.getItem('authToken');
  },
  
  getRole: () => {
    return localStorage.getItem('userRole') || null;
  },
  
  setRole: (role) => {
    if (role) {
      localStorage.setItem('userRole', role);
    }
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },
};

// Service pour les patients
export const patientService = {
  getAll: (params = {}) => api.get('/patients/', { params }),
  getById: (id) => api.get(`/patients/${id}/`),
  create: (data) => api.post('/patients/', data),
  update: (id, data) => api.put(`/patients/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/patients/${id}/`, data),
  delete: (id) => api.delete(`/patients/${id}/`),
};

// Service pour les radiographies
export const radiographyService = {
  getAll: (params = {}) => api.get('/radiographies/', { params }),
  getById: (id) => api.get(`/radiographies/${id}/`),
  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'image' && data[key]) {
        formData.append('image', data[key]);
      } else if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.post('/radiographies/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  update: (id, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'image' && data[key]) {
        formData.append('image', data[key]);
      } else if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.put(`/radiographies/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  partialUpdate: (id, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'image' && data[key]) {
        formData.append('image', data[key]);
      } else if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.patch(`/radiographies/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (id) => api.delete(`/radiographies/${id}/`),
};

// Service pour les rendez-vous
export const appointmentService = {
  getAll: (params = {}) => api.get('/appointments/', { params }),
  getById: (id) => api.get(`/appointments/${id}/`),
  create: (data) => api.post('/appointments/', data),
  update: (id, data) => api.put(`/appointments/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/appointments/${id}/`, data),
  delete: (id) => api.delete(`/appointments/${id}/`),
};

// Service pour les traitements
export const treatmentService = {
  getAll: (params = {}) => api.get('/treatments/', { params }),
  getById: (id) => api.get(`/treatments/${id}/`),
  create: (data) => api.post('/treatments/', data),
  update: (id, data) => api.put(`/treatments/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/treatments/${id}/`, data),
  delete: (id) => api.delete(`/treatments/${id}/`),
};

// Service pour les plans de traitement
export const treatmentPlanService = {
  getAll: (params = {}) => api.get('/treatment-plans/', { params }),
  getById: (id) => api.get(`/treatment-plans/${id}/`),
  create: (data) => api.post('/treatment-plans/', data),
  update: (id, data) => api.put(`/treatment-plans/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/treatment-plans/${id}/`, data),
  delete: (id) => api.delete(`/treatment-plans/${id}/`),
};

// Service pour les factures
export const invoiceService = {
  getAll: (params = {}) => api.get('/invoices/', { params }),
  getById: (id) => api.get(`/invoices/${id}/`),
  create: (data) => api.post('/invoices/', data),
  update: (id, data) => api.put(`/invoices/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/invoices/${id}/`, data),
  delete: (id) => api.delete(`/invoices/${id}/`),
};

// Service pour les paiements
export const paymentService = {
  getAll: (params = {}) => api.get('/payments/', { params }),
  getById: (id) => api.get(`/payments/${id}/`),
  create: (data) => api.post('/payments/', data),
  update: (id, data) => api.put(`/payments/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/payments/${id}/`, data),
  delete: (id) => api.delete(`/payments/${id}/`),
};

// Service pour les devis
export const quoteService = {
  getAll: (params = {}) => api.get('/quotes/', { params }),
  getById: (id) => api.get(`/quotes/${id}/`),
  create: (data) => api.post('/quotes/', data),
  update: (id, data) => api.put(`/quotes/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/quotes/${id}/`, data),
  delete: (id) => api.delete(`/quotes/${id}/`),
};

// Service pour le personnel
export const staffService = {
  getAll: (params = {}) => api.get('/staff/', { params }),
  getById: (id) => api.get(`/staff/${id}/`),
  create: (data) => api.post('/staff/', data),
  update: (id, data) => api.put(`/staff/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/staff/${id}/`, data),
  delete: (id) => api.delete(`/staff/${id}/`),
};

// Service pour les prothèses
export const prosthesisService = {
  getAll: (params = {}) => api.get('/prostheses/', { params }),
  getById: (id) => api.get(`/prostheses/${id}/`),
  create: (data) => api.post('/prostheses/', data),
  update: (id, data) => api.put(`/prostheses/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/prostheses/${id}/`, data),
  delete: (id) => api.delete(`/prostheses/${id}/`),
};

// Service pour les fournisseurs
export const supplierService = {
  getAll: (params = {}) => api.get('/suppliers/', { params }),
  getById: (id) => api.get(`/suppliers/${id}/`),
  create: (data) => api.post('/suppliers/', data),
  update: (id, data) => api.put(`/suppliers/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/suppliers/${id}/`, data),
  delete: (id) => api.delete(`/suppliers/${id}/`),
};

// Service pour les achats
export const purchaseService = {
  getAll: (params = {}) => api.get('/purchases/', { params }),
  getById: (id) => api.get(`/purchases/${id}/`),
  create: (data) => api.post('/purchases/', data),
  update: (id, data) => api.put(`/purchases/${id}/`, data),
  partialUpdate: (id, data) => api.patch(`/purchases/${id}/`, data),
  delete: (id) => api.delete(`/purchases/${id}/`),
  getStatistics: () => api.get('/purchases/statistics/'),
};

// Service pour les produits de stock
export const productService = {
  getAll: (params = {}) => api.get('/products/', { params }),
  getById: (id) => api.get(`/products/${id}/`),
  create: (data) => {
    // Si l'image est présente, utiliser FormData
    if (data.image && data.image instanceof File) {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'image' && data[key]) {
          formData.append('image', data[key]);
        } else if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });
      return api.post('/products/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    // Sinon, envoyer en JSON
    return api.post('/products/', data);
  },
  update: (id, data) => {
    if (data.image && data.image instanceof File) {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'image' && data[key]) {
          formData.append('image', data[key]);
        } else if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });
      return api.put(`/products/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.put(`/products/${id}/`, data);
  },
  partialUpdate: (id, data) => api.patch(`/products/${id}/`, data),
  delete: (id) => api.delete(`/products/${id}/`),
  getStatistics: () => api.get('/products/statistics/'),
  getSuppliers: () => api.get('/products/suppliers/'),
};

export default api;

